import { t } from '@bms2/shared';
import { BLOB_TAB_REVOKE_MS } from '../reports/api';
import { SHIPMENTS_PATH, apiFetch } from './api';
import styles from './ViewPdfLink.module.css';

/** The sheet one document renders on: its own route plus `/pdf`, the one shape every document type answers. */
export function documentPdfPath(basePath: string, documentId: string): string {
  return `${basePath}/${documentId}/pdf`;
}

/** 🔨 The packing slip is the Shipment's own sheet, not the Purchase Order's, so it hangs off the shipment (piece 27). */
export function packingSlipPath(shipmentId: string): string {
  return `${SHIPMENTS_PATH}/${shipmentId}/packing-slip.pdf`;
}

/**
 * The name a sheet's answer gives itself in its Content-Disposition (piece 30,
 * T3658): the RFC 5987 `filename*` first, then the plain `filename`, or null
 * where the answer names none.
 */
export function sheetFilename(disposition: string | null | undefined): string | null {
  if (!disposition) return null;
  const extended = /filename\*\s*=\s*([^']*)'[^']*'([^;]+)/i.exec(disposition);
  if (extended && /^utf-8$/i.test(extended[1]!.trim())) {
    try {
      const name = decodeURIComponent(extended[2]!.trim()).trim();
      if (name) return name;
    } catch {
      // silent: a malformed encoded name falls back to the plain one beside it.
    }
  }
  const plain = /filename\s*=\s*(?:"((?:[^"\\]|\\.)*)"|([^;\s]+))/i.exec(disposition);
  const name = (plain?.[1]?.replace(/\\(.)/g, '$1') ?? plain?.[2] ?? '').trim();
  return name || null;
}

/** What opening a sheet came to: the tab shows it, or the words saying why it does not. */
export type SheetOutcome = { outcome: 'opened' } | { outcome: 'failed' | 'blocked'; message: string };

/**
 * A tab of its own for a sheet, opened at the press while the browser still
 * counts it as the person's act (decision 1130). A pop-up blocker answers
 * null. The tab is cut off from this page at once, as `noopener` did, and
 * shows nothing until the sheet's bytes arrive.
 */
export function reserveSheetTab(): Window | null {
  if (typeof window.open !== 'function') return null;
  const tab = window.open('', '_blank') ?? null;
  if (tab) {
    try {
      tab.opener = null;
    } catch {
      // silent: a browser that refuses the assignment keeps the tab, which still only shows the sheet
      // A browser that refuses the assignment keeps the tab; it still only ever shows the sheet.
    }
  }
  return tab;
}

/** The server's own sentence for a refused sheet, or the plain one where it sent none. */
async function refusalOf(response: Response): Promise<string> {
  const payload = typeof response.json === 'function' ? ((await response.json().catch(/* silent: a body that is not JSON reads as none, and the status check beside it answers the failure. */ () => null)) as { message?: string | string[] } | null) : null;
  const message = Array.isArray(payload?.message) ? payload.message.join('; ') : payload?.message;
  return message ? `${t('sheet.failed')} ${message}` : t('sheet.failed');
}

/** A sheet that did not come: the tab reserved for it says why, rather than standing blank. */
function failed(tab: Window | null, message: string): SheetOutcome {
  if (tab && !tab.closed) {
    try {
      tab.document.title = t('sheet.failed');
      tab.document.body.textContent = message;
    } catch {
      // silent: a tab the browser will not write into is closed, and the page keeps its notice.
      tab.close();
    }
  }
  return { outcome: 'failed', message };
}

/**
 * 🔒 Opens a rendered sheet in the browser's own PDF viewer, in a tab of its
 * own (piece 27; decisions 0686, 1130). Every caller that opens a sheet — View
 * PDF, a packing slip, a sent statement, a check batch, a report's Export to
 * PDF, a template's Print example, Pay and Print Now — opens it here, the
 * one way.
 *
 * 🔒 The bytes are read through `apiFetch` and the tab is handed a blob URL of
 * them, so no browser setting turns the sheet into a download (T439). The
 * owner, 2026-09-27: *"on print it is making a download it should be the blob
 * for the printing"*. The read carries the View-as headers and the session a
 * bare address never did.
 *
 * 🔨 The tab is reserved at the press, before the read, because a tab opened
 * after a wait is one a pop-up blocker refuses. A caller whose press starts a
 * write first reserves it itself and hands it in.
 *
 * 🔨 The name the answer's Content-Disposition gives travels as a File's name
 * (T3658). Chromium's viewer still offers the blob URL's own id on save
 * (piece 30's open issue).
 *
 * 🔨 A refused read writes its sentence into that tab and answers it, so the
 * caller can say it too; a blocked tab answers `blocked`. The blob URL is
 * revoked once the tab has read it.
 */
export async function openSheet(url: string, options: { tab?: Window | null; fetcher?: (url: string) => Promise<Response> } = {}): Promise<SheetOutcome> {
  const tab = options.tab === undefined ? reserveSheetTab() : options.tab;
  let response: Response;
  try {
    response = await (options.fetcher ?? apiFetch)(url);
  } catch {
    // silent: a fetch the server never answered cannot reach the server's log either; the page says it could not reach the server.
    return failed(tab, t('sheet.failed'));
  }
  if (!response.ok) return failed(tab, await refusalOf(response));
  if (typeof response.blob !== 'function' || typeof URL.createObjectURL !== 'function') return failed(tab, t('sheet.failed'));
  const bytes = await response.blob();
  // 🔨 Typed as a PDF whatever the answer said, so the tab draws the viewer rather than offering a save of its own.
  const typed = bytes.type === 'application/pdf' ? bytes : new Blob([bytes], { type: 'application/pdf' });
  // The name the answer gave travels with the bytes as a File (T3658), for a browser that names a blob's save by it.
  const name = sheetFilename(response.headers?.get?.('content-disposition'));
  const pdf = name && typeof File === 'function' ? new File([typed], name, { type: 'application/pdf' }) : typed;
  const objectUrl = URL.createObjectURL(pdf);
  let shown = false;
  if (tab && !tab.closed) {
    try {
      tab.location.href = objectUrl;
      shown = true;
    } catch {
      // silent: a tab the browser will not send to the sheet is answered by the download that follows.
      shown = false;
    }
  }
  if (!shown && typeof window.open === 'function') shown = Boolean(window.open(objectUrl, '_blank'));
  if (!shown) {
    URL.revokeObjectURL(objectUrl);
    return { outcome: 'blocked', message: t('sheet.blocked') };
  }
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), BLOB_TAB_REVOKE_MS);
  return { outcome: 'opened' };
}

/**
 * 🔒 View PDF, the action every accounting document page carries (piece 27;
 * decisions 0557, 0686). It opens the sheet in the browser's own PDF viewer,
 * the way the Print button already did, rather than in a viewer this
 * application draws. The product owner, 2026-09-23: *"no need for the print
 * modal window at all in the system in the places it are called please launch
 * the pdf viewer native in the browser like the print button does anyways."*
 *
 * 🔨 The path is relative to the API root and never repeats `/api` — the base
 * URL already carries it behind the test environment's proxy.
 *
 * 🔨 It draws only in view mode and only on a document whose answer came back,
 * which is the gate: a viewer who cannot read the document never reaches the
 * page that would offer them its sheet.
 *
 * 🔨 A caller that is not a document's own page names the action itself: the
 * print template's sample sheet is a Print example rather than a View PDF, and
 * it is the same control opening the same tab (decision 0571).
 */
export function ViewPdfLink({ path, apiBaseUrl = '', documentType, label }: { path: string; apiBaseUrl?: string; documentType: string; label?: string }) {
  return (
    <button type="button" className={styles.pdf} onClick={() => void openSheet(`${apiBaseUrl}${path}`)} data-document-pdf={documentType}>
      {label ?? t('po.action.pdf')}
    </button>
  );
}
